import { inject, observer } from 'mobx-react';
import React, { Component } from 'react';

import { ImpuestosAguaIngresosPorUsoRecord, ImpuestosAguaPagosRecord } from '../../models';
import { ImpuestosAguaMedidorStore } from '../../stores';

import { ColumnProps } from 'antd/lib/table';
import styled from 'styled-components';
import { Table, TableNumberCell, TotalTable } from '../shared';
import { SubformSaveButton } from '../shared/SubformSaveButton';
import { TextBlock } from '../shared/TextBlock';

interface IngresosPorUsoSubformProps {
    isReadonly: boolean;
    impuestosAguaPorUsoStore?: ImpuestosAguaMedidorStore;
}

const Header = styled(Table)`
    .ant-table-placeholder {
        display: none;
    }
`;

@inject('impuestosAguaPorUsoStore')
@observer
export class IngresosPorUsoSubform extends Component<IngresosPorUsoSubformProps> {
    AguaCountColumns = (
        type: string,
        subtitle1?: string,
        subtitle2?: string
    ): Array<ColumnProps<ImpuestosAguaPagosRecord>> => [
        {
            title: <TextBlock text={`N° DE\\nUSUARIOS${subtitle1 ? `\\n(${subtitle1})` : ''}`} />,
            dataIndex: `${type}.usuario`,
            width: '12.5%',
            render: (_, record) =>
                this.renderIngresosPorUso(`${type}.usuario`, record[
                    `${type}.usuario` as keyof ImpuestosAguaPagosRecord
                ] as number)
        },
        {
            title: <TextBlock text={`N° DE\\nTOMAS${subtitle2 ? `\\n(${subtitle2})` : ''}`} />,
            dataIndex: `${type}.toma`,
            align: 'right',
            width: '12.5%',
            render: (_, record) =>
                this.renderIngresosPorUso(`${type}.toma`, record[
                    `${type}.toma` as keyof ImpuestosAguaPagosRecord
                ] as number)
        }
    ];

    IngresosPorUsoDePagosColumns = (
        ejercicio: number
    ): Array<ColumnProps<ImpuestosAguaPagosRecord>> => [
        {
            title: (
                <TextBlock
                    text={`N° DE USUARIOS Y N° DE TOMAS QUE REALIZARON ÚNICAMENTE PAGOS DE ${ejercicio}\\n(C)`}
                />
            ),
            dataIndex: 'numeroDePagosEjercicio',
            children: this.AguaCountColumns('numeroDePagosEjercicio', 'C1', 'C2')
        },
        {
            title: (
                <TextBlock
                    text={`N° DE USUARIOS Y N° DE TOMAS QUE REALIZARON ÚNICAMENTE PAGOS DE EJERCICIOS ANTERIORES A ${ejercicio}\\n(D)`}
                />
            ),
            dataIndex: 'numeroDePagosEjercicioAnterior',
            children: this.AguaCountColumns('numeroDePagosEjercicioAnterior', 'D1', 'D2')
        },
        {
            title: (
                <TextBlock
                    text={`N° DE USUARIOS Y N° DE TOMAS QUE REALIZARON TANTO PAGOS DE ${ejercicio} COMO DE EJERCICIOS ANTERIORES A ${ejercicio}\\n(E)`}
                />
            ),
            dataIndex: 'numeroDePagosEjercicioMixto',
            children: this.AguaCountColumns('numeroDePagosEjercicioMixto', 'E1', 'E2')
        },
        {
            title: (
                <TextBlock text="N° DE USUARIOS Y N° DE TOMAS QUE REALIZARON PAGOS EN EL MES\n(F=C+D+E)" />
            ),
            dataIndex: 'pagosTotal',
            children: this.AguaCountColumns('pagosTotal', 'F1=C1+D1+E1', 'F2=C2+D2+E2')
        }
    ];

    IngresosPorUsoHeaderColumns = (ejercicio: number): Array<ColumnProps<{}>> => [
        {
            title: <TextBlock text="CONCEPTO" />,
            key: 'concepto_ingresosporuso'
        },
        {
            title: (
                <TextBlock
                    text={`RECAUDACIÓN DEL EJERCICIO ${ejercicio} Y DE\\nEJERCICIOS ANTERIORES A ${ejercicio}`}
                />
            ),
            key: 'ingresosporuso',
            children: [
                {
                    title: <TextBlock text="N° DE\nUSUARIOS\n(A)" />,
                    key: 0 + 'ingresosporuso',
                    width: 200
                },
                {
                    title: <TextBlock text="N° DE\nTOMAS\n(B)" />,
                    key: 1 + 'ingresosporuso',
                    width: 200
                },
                {
                    title: (
                        <div>
                            <TextBlock text="IMPORTE\n(PESOS)" />
                        </div>
                    ),
                    key: 2 + 'ingresosporuso',
                    width: 200
                }
            ]
        }
    ];

    IngresosPorUsoExistenteColumns = (
        type: 'ConMedidor' | 'SinMedidor' | 'TOTAL'
    ): Array<ColumnProps<ImpuestosAguaIngresosPorUsoRecord>> => [
        {
            dataIndex: 'concepto',
            render: (text, record) => ({
                props: {
                    className:
                        record.key === 'ConMedidortotal' || record.key === 'SinMedidortotal'
                            ? 'total-cell'
                            : undefined
                },
                children: text
            })
        },
        {
            children: [
                {
                    dataIndex: 'usuario',
                    width: 200,
                    render: (_, record) =>
                        this.renderIngresosPorUso(
                            `ingresosPorTiposUso${type}.${record.key}.usuario`,
                            record.usuario
                        )
                },
                {
                    dataIndex: 'toma',
                    width: 200,
                    render: (_, record) =>
                        this.renderIngresosPorUso(
                            `ingresosPorTiposUso${type}.${record.key}.toma`,
                            record.toma
                        )
                },
                {
                    dataIndex: 'importe',
                    width: 200,
                    render: (_, record) =>
                        this.renderIngresosPorUso(
                            `ingresosPorTiposUso${type}.${record.key}.importe`,
                            record.importe
                        )
                }
            ]
        }
    ];

    renderConMedidorTitle = () => 'USO DE LA BASE CON MEDIDOR';

    renderSinMedidorTitle = () => 'USO DE LA BASE SIN MEDIDOR';

    renderIngresosPorUso = (path: string, value: number) => {
        const { isReadonly, impuestosAguaPorUsoStore } = this.props;
        const { setIngresosPorUsoValue } = impuestosAguaPorUsoStore!;

        const isToma = path.toLowerCase().includes('toma');
        const isUsuario = path.toLowerCase().includes('usuario');
        const isTotal = path.toLowerCase().includes('total');

        return (
            <TableNumberCell
                type={isToma || isUsuario ? 'integer' : 'decimal'}
                isReadonly={isReadonly || isTotal || isToma}
                path={path}
                value={value}
                onChange={setIngresosPorUsoValue}
            />
        );
    };

    render() {
        const { IngresosPorUsoExistenteColumns } = this;
        const { isReadonly, impuestosAguaPorUsoStore } = this.props;
        const {
            ejercicio,
            IngresosPorUsoConMediadorRecords,
            IngresosPorUsoSinMediadorRecords,
            IngresosPorUsoTotalRecords,
            IngresosPorUsoPagos,
            isSaving,
            isLoading,
            saveMedidor
        } = impuestosAguaPorUsoStore!;

        const [_, ...ingresosPorUsoExistenteTotalValueColumns] = IngresosPorUsoExistenteColumns(
            'TOTAL'
        );

        return (
            <>
                <Header columns={this.IngresosPorUsoHeaderColumns(ejercicio)} />
                <TextBlock text="DERECHOS POR SUMINISTRO DE AGUA\nINGRESOS" />
                <Table
                    title={this.renderConMedidorTitle}
                    dataSource={IngresosPorUsoConMediadorRecords}
                    columns={IngresosPorUsoExistenteColumns('ConMedidor')}
                    showHeader={false}
                    loading={isLoading}
                />
                <Table
                    title={this.renderSinMedidorTitle}
                    dataSource={IngresosPorUsoSinMediadorRecords}
                    columns={IngresosPorUsoExistenteColumns('SinMedidor')}
                    showHeader={false}
                    loading={isLoading}
                />
                <TotalTable
                    dataSource={IngresosPorUsoTotalRecords}
                    valueColumns={ingresosPorUsoExistenteTotalValueColumns}
                    showHeader={false}
                    loading={isLoading}
                />
                <Table
                    columns={this.IngresosPorUsoDePagosColumns(ejercicio)}
                    dataSource={IngresosPorUsoPagos}
                    loading={isLoading}
                />

                {!isReadonly && <SubformSaveButton isSaving={isSaving} onSave={saveMedidor} />}
            </>
        );
    }
}
